/**
 * The bank feed aggregators (piece 24; decision 0487): the secrets the Plaid
 * and Stripe adapters behind the `BankFeedProvider` seam open, held sealed in
 * the `KeyProvider` envelope beside the payment, Maps and Vision keys.
 *
 * 🔨 Both are built and neither is chosen here (product owner, 2026-09-17:
 * *"Plaid is ok to build into the banking connection seam as a option. But id
 * like both to be built"*). The seam is many-active by decision 0032, so which
 * aggregator an account runs through is that account's own `feedProviderKey`,
 * and an environment holding no secret for a provider offers it nowhere.
 *
 * 🔨 No key is held in the repository, in an environment variable or in a
 * fixture. An environment with neither secret runs the scripted fake and pulls
 * nothing from anybody's live account.
 */

/** Our own key for the Plaid adapter, the value a `BankAccount.feedProviderKey` carries. */
export const PLAID_FEED_KEY = 'plaid';

/** Our own key for the Stripe Financial Connections adapter; distinct from the payment gateway's `stripe`, which is a different seam. */
export const STRIPE_FEED_KEY = 'stripeFinancialConnections';

/** Plaid issues a pair, and both are needed on every call: the client identifies us and the secret authenticates us. */
export const PLAID_CLIENT_ID_KEY = 'banking.plaidClientId';
export const PLAID_SECRET_KEY = 'banking.plaidSecret';

/**
 * Which Plaid host the calls go to. 🔨 It is a setting rather than a build
 * flag, because the sandbox and the production host take different secrets and
 * an environment can hold either.
 */
export const PLAID_ENVIRONMENT_KEY = 'banking.plaidEnvironment';

/**
 * 🔒 The Plaid API version every call is pinned to. Plaid versions its API and
 * an unpinned caller gets whatever the dashboard's default happens to be, which
 * a person can change without touching this repository — and the sync
 * endpoint's own shape moves between versions (found while building,
 * 2026-09-17). A pin is the difference between an upgrade we choose and one
 * that arrives.
 */
export const PLAID_API_VERSION = '2020-09-14';

export const PLAID_ENVIRONMENTS = ['sandbox', 'production'] as const;
export type PlaidEnvironment = (typeof PLAID_ENVIRONMENTS)[number];

/** The host each environment answers on; an unknown value is the sandbox, never production. */
export function plaidHost(environment: string | null | undefined): string {
  return environment === 'production' ? 'https://production.plaid.com' : 'https://sandbox.plaid.com';
}

/**
 * 🔨 Stripe Financial Connections runs on the same secret as the payment
 * gateway, because it is the same Stripe account. A second key row would let
 * the two drift apart and leave nothing saying which one is current.
 */
export const STRIPE_FEED_USES_PAYMENT_SECRET = true;
